import { Icon, tokens } from '@gripp/shared-ui';
import { useEffect, useRef } from 'react';
import {
  ActivityIndicator,
  Animated,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';

interface IncomingCallModalProps {
  callerName: string;
  /** Initials shown in the avatar, e.g. "PJ". */
  callerInitials: string;
  onAccept: () => void;
  onDecline: () => void;
  responding: boolean;
}

/**
 * Full-screen "X is calling you" prompt with Accept / Decline, styled after
 * the gripp-os CallOverlay reference (dark full-bleed background, pulsing
 * initials avatar, circular action buttons with captions). Mounted by
 * CallProvider when an incoming-call signal arrives.
 *
 * This is the RN-native port of the web IncomingCallModal — the web version is
 * already written against react-native primitives, so the surface is identical.
 * Accept/decline never navigate; CallProvider simply swaps this Modal for the
 * CallRoom Modal in place, so the `router.push`-from-inside-a-Modal occlusion
 * trap (header profile dropdown) does not apply here.
 */
export const IncomingCallModal = ({
  callerName,
  callerInitials,
  onAccept,
  onDecline,
  responding,
}: IncomingCallModalProps) => {
  const pulseAnim = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    const loopAnim = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 0.5,
          duration: 1000,
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: true,
        }),
      ])
    );

    loopAnim.start();

    return () => loopAnim.stop();
  }, [pulseAnim]);

  return (
    <Modal visible animationType="fade" onRequestClose={onDecline}>
      <View style={styles.container}>
        <View style={styles.callerBlock}>
          <Animated.View style={[styles.avatar, { opacity: pulseAnim }]}>
            <Text style={styles.avatarText}>{callerInitials}</Text>
          </Animated.View>
          <View style={styles.textBlock}>
            <Text style={styles.name} numberOfLines={1}>
              {callerName}
            </Text>
            <Text style={styles.subtitle}>is calling you…</Text>
          </View>
        </View>
        <View style={styles.actions}>
          <View style={styles.actionSlot}>
            <Pressable
              onPress={onDecline}
              disabled={responding}
              accessibilityRole="button"
              accessibilityLabel="Decline call"
              style={[
                styles.actionButton,
                styles.declineButton,
                responding && styles.actionButtonDisabled,
              ]}
            >
              <Icon
                name="PhoneCall"
                size={26}
                color={tokens.colors.brand.white}
                style={styles.declineIcon}
              />
            </Pressable>
            <Text style={styles.actionLabel}>Decline</Text>
          </View>
          <View style={styles.actionSlot}>
            <Pressable
              onPress={onAccept}
              disabled={responding}
              accessibilityRole="button"
              accessibilityLabel="Accept call"
              style={[styles.actionButton, styles.acceptButton]}
            >
              {responding ? (
                <ActivityIndicator color={tokens.colors.brand.white} />
              ) : (
                <Icon name="PhoneCall" size={26} color={tokens.colors.brand.white} />
              )}
            </Pressable>
            <Text style={styles.actionLabel}>Accept</Text>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: tokens.colors.slate[800],
    paddingHorizontal: tokens.spacing['4xl'],
    paddingTop: tokens.spacing['6xl'],
    paddingBottom: tokens.spacing['4xl'],
  },
  callerBlock: {
    alignItems: 'center',
    gap: tokens.spacing['2xl'],
    marginTop: tokens.spacing['6xl'],
  },
  avatar: {
    width: 96,
    height: 96,
    borderRadius: 48,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  avatarText: {
    ...tokens.typography.headline.small,
    color: 'rgba(255, 255, 255, 0.7)',
  },
  textBlock: {
    alignItems: 'center',
    gap: tokens.spacing.xs,
  },
  name: {
    ...tokens.typography.title.medium,
    color: tokens.colors.brand.white,
  },
  subtitle: {
    ...tokens.typography.body.medium,
    color: tokens.colors.neutral[400],
  },
  actions: {
    flexDirection: 'row',
    gap: tokens.spacing['6xl'],
  },
  actionSlot: {
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  actionButton: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  acceptButton: {
    backgroundColor: tokens.colors.semantic.success,
  },
  declineButton: {
    backgroundColor: tokens.colors.semantic.error,
  },
  actionButtonDisabled: {
    opacity: 0.5,
  },
  declineIcon: {
    transform: [{ rotate: '135deg' }],
  },
  actionLabel: {
    ...tokens.typography.label.medium,
    color: 'rgba(255, 255, 255, 0.8)',
  },
});
